/**
 * UI — a thin DOM overlay on top of the Pixi canvas.
 *
 * Rationale for DOM-over-Pixi: menus with scrollable codex text, pricing
 * rows, and settings sliders are dramatically cheaper to build and style in
 * HTML/CSS. Visual rules (semi-transparent rounded organic shapes, Inter
 * font) are enforced in ui.css. In-world UI (floating numbers, the event
 * banner's scale animation, the cell itself) stays in Pixi.
 *
 * All panels and the HUD are driven by the EventBus; nothing reads from
 * game systems directly here.
 */

import { bus } from '@/core/EventBus';
import { saveManager } from '@/core/SaveManager';
import type { UpgradeSystem } from '@/systems/UpgradeSystem';
import type { MutationSystem } from '@/systems/MutationSystem';
import { MAX_SLOTS } from '@/systems/MutationSystem';
import type { CodexSystem } from '@/systems/CodexSystem';
import type { EnvironmentSystem } from '@/systems/EnvironmentSystem';
import type { CodexEntry, EnvironmentalEventDef, MutationDef } from '@/data/types';
import { player } from '@/systems/PlayerState';
import { VERSION } from '@/version';
import { describeScoreResult } from '@/score/format';

import './ui.css';

export interface UISystems {
  upgrades: UpgradeSystem;
  mutations: MutationSystem;
  codex: CodexSystem;
  environment: EnvironmentSystem;
  restart: () => void;
  onPulse?: () => void;
  onDash?: () => void;
  stageTarget: { biomass: number; time: number };
  events: Record<string, EnvironmentalEventDef>;
  /** Map of codex entry id -> rendered icon canvas. Same art as in-game. */
  codexIcons?: Map<string, HTMLCanvasElement>;
}

const IS_TOUCH = typeof window !== 'undefined'
  && (window.matchMedia?.('(hover: none) and (pointer: coarse)').matches
      || 'ontouchstart' in window);

export class UI {
  private root: HTMLElement;
  private hud!: HTMLElement;
  private banner!: HTMLElement;
  private bannerHideTimer = 0;
  private goalBanner!: HTMLElement;
  private goalBannerHide = 0;
  // Stage transitions dispose this UI and build a fresh one for the
  // next stage. Each bus.on returns an unsubscribe; pushing them here
  // lets destroy() detach cleanly so handlers don't keep firing on a
  // detached DOM after we've moved on.
  private removers: (() => void)[] = [];
  private toasts!: HTMLElement;
  private panels: Record<string, HTMLElement> = {};
  private comboEl!: HTMLElement;
  private timerEl!: HTMLElement;
  private stageCompleteEl!: HTMLElement;
  private deathOverlay!: HTMLElement;
  private deathBanner!: HTMLElement;
  private pauseOverlay!: HTMLElement;
  private paused = false;

  private hudBtns: Record<'upgrades' | 'mutations' | 'codex', HTMLElement> = {} as Record<'upgrades' | 'mutations' | 'codex', HTMLElement>;
  private unseenCodex = new Set<string>();
  private alertTimer = 0;
  // Cache of codex icon data URLs, computed lazily so the canvas → PNG
  // conversion happens once per icon and the resulting <img> can appear
  // in both the codex menu and toasts simultaneously.
  private iconUrlCache = new Map<string, string>();

  // cached DOM refs for HUD stat bars
  private hpBar!: HTMLElement; private hpVal!: HTMLElement;
  private atpBar!: HTMLElement; private atpVal!: HTMLElement;
  private biomassVal!: HTMLElement;
  private goalText!: HTMLElement;
  private goalFill!: HTMLElement;
  private dnaVal!: HTMLElement;

  private stageElapsed = 0;

  constructor(private sys: UISystems) {
    this.root = document.createElement('div');
    this.root.id = 'ui';
    document.body.appendChild(this.root);
    this.buildHUD();
    this.buildBanner();
    this.buildGoalBanner();
    this.buildToasts();
    this.buildPanels();
    this.buildStageComplete();
    this.buildCursor();
    this.buildAbilityPad();
    this.buildPauseOverlay();
    this.wireEvents();
    this.wirePauseKeys();
  }

  tick(dt: number): void {
    this.stageElapsed = saveManager.getRun().scoreTime;
    const total = Math.floor(this.stageElapsed);
    const mm = Math.floor(total / 60).toString().padStart(2, '0');
    const ss = (total % 60).toString().padStart(2, '0');
    // Forward-counting only. The win condition is biomass-only; elapsed
    // determines the carryover speed bonus, so a target-time fraction
    // would mislead the player into thinking the timer gates the run.
    this.timerEl.textContent = `${mm}:${ss}`;
    if (this.goalBannerHide > 0) {
      this.goalBannerHide -= dt;
      if (this.goalBannerHide <= 0) this.goalBanner.classList.remove('visible');
    }

    // event banner countdown during warning phase
    const env = this.sys.environment;
    if (env.getPhase() === 'warning') {
      const ev = env.currentEvent();
      if (ev) {
        const secs = Math.max(0, Math.ceil(env.timeLeft()));
        this.setBannerCountdown(ev, secs);
      }
    }

    if (this.bannerHideTimer > 0) {
      this.bannerHideTimer -= dt;
      if (this.bannerHideTimer <= 0) this.banner.classList.remove('visible');
    }

    // HUD alert badges refresh a few times per second — biomass/DNA change
    // constantly so a per-frame recompute is wasteful, and nothing cares
    // about sub-half-second latency on a "!" dot.
    this.alertTimer -= dt;
    if (this.alertTimer <= 0) {
      this.alertTimer = 0.33;
      this.refreshHudAlerts();
    }
  }

  private refreshHudAlerts(): void {
    const upgrades = this.sys.upgrades.allDefs().some(d => this.sys.upgrades.canBuy(d.id));

    let mutations = false;
    const muts = this.sys.mutations;
    const hasOpenSlot = muts.hasOpenSlot();
    // Affordable rank-up on something already owned.
    for (const id of muts.ownedIds()) {
      if (player.dna >= muts.rankCost(id)) { mutations = true; break; }
    }
    // Affordable first-pick when a slot is open.
    if (!mutations && hasOpenSlot) {
      for (const m of muts.allDefs()) {
        if (muts.isOwned(m.id)) continue;
        if (player.dna >= m.pickCost) { mutations = true; break; }
      }
    }
    // Affordable next slot.
    if (!mutations && player.dna >= muts.slotCost()) mutations = true;

    this.hudBtns.upgrades.classList.toggle('alert', upgrades);
    this.hudBtns.mutations.classList.toggle('alert', mutations);
    this.hudBtns.codex.classList.toggle('alert', this.unseenCodex.size > 0);
  }

  // --------------- HUD ---------------
  private buildHUD(): void {
    this.hud = el('div', { id: 'hud' }, [
      el('div', { class: 'hud-left' }, [
        this.hudStat('biomass', 'Biomass', '0'),
        this.hudStat('dna', 'DNA', '0'),
        this.hudStatBar('hp', 'Health', '100/100'),
        this.hudStatBar('atp', 'ATP', '100/100'),
      ]),
      el('div', { class: 'hud-right' }, [
        el('div', { id: 'hud-timer', class: 'hud-stat' }),
        (this.hudBtns.upgrades = this.hudButton('📊', 'Upgrades', () => this.toggle('upgrades'))),
        (this.hudBtns.mutations = this.hudButton('🧬', 'Mutations', () => this.toggle('mutations'))),
        (this.hudBtns.codex = this.hudButton('📖', 'Codex', () => this.toggle('codex'))),
      ]),
    ]);
    // Two slots so combo (bloom-clears) and streak (same-food chain) can
    // update independently without overwriting each other's text — that
    // collision was the source of "stuck" combo readouts.
    this.comboEl = el('div', { id: 'hud-combo' }, [
      el('span', { class: 'combo-main' }),
      el('span', { class: 'combo-streak' }),
    ]);
    this.timerEl = this.hud.querySelector('#hud-timer') as HTMLElement;

    // cache stat refs
    this.biomassVal = this.hud.querySelector('.hud-stat.biomass .value') as HTMLElement;
    this.dnaVal = this.hud.querySelector('.hud-stat.dna .value') as HTMLElement;
    this.hpVal = this.hud.querySelector('.hud-stat.hp .value') as HTMLElement;
    this.hpBar = this.hud.querySelector('.hud-stat.hp .bar > span') as HTMLElement;
    this.atpVal = this.hud.querySelector('.hud-stat.atp .value') as HTMLElement;
    this.atpBar = this.hud.querySelector('.hud-stat.atp .bar > span') as HTMLElement;

    // Stage goal bar — top-center, mirrors Phase 2's biomass-to-goal bar.
    const goal = el('div', { id: 'hud-goal' }, [
      el('div', { class: 'goal-text' }),
      el('div', { class: 'goal-bar' }, [el('span')]),
    ]);
    this.goalText = goal.querySelector('.goal-text') as HTMLElement;
    this.goalFill = goal.querySelector('.goal-bar > span') as HTMLElement;
    this.updateGoalBar(player.biomass);

    this.root.appendChild(this.hud);
    this.root.appendChild(goal);
    this.root.appendChild(this.comboEl);

    const versionTag = el('div', { id: 'version-tag' }, [], `v${VERSION}`);
    versionTag.setAttribute('style',
      'position:fixed;bottom:6px;right:8px;font:11px/1 Inter,system-ui,sans-serif;' +
      'color:rgba(180,220,230,0.55);letter-spacing:0.5px;pointer-events:none;' +
      'user-select:none;text-shadow:0 0 4px rgba(0,0,0,0.6);z-index:9999;');
    this.root.appendChild(versionTag);

    // Mute + fullscreen now live in the global toolbar (top-left), shared
    // across stages so phase 2 gets them too.
  }

  private updateGoalBar(biomass: number): void {
    const target = this.sys.stageTarget.biomass;
    const pct = Math.min(1, Math.max(0, biomass / target));
    this.goalText.innerHTML =
      `Biomass&nbsp; <b>${Math.floor(biomass).toLocaleString()}</b> / ${target.toLocaleString()}`;
    this.goalFill.style.width = `${(pct * 100).toFixed(2)}%`;
  }

  private hudStat(cls: string, label: string, value: string): HTMLElement {
    return el('div', { class: `hud-stat ${cls}` }, [
      el('span', { class: 'label' }, [], label),
      el('span', { class: 'value' }, [], value),
    ]);
  }
  private hudStatBar(cls: string, label: string, value: string): HTMLElement {
    return el('div', { class: `hud-stat ${cls}` }, [
      el('span', { class: 'label' }, [], label),
      el('span', { class: 'value' }, [], value),
      el('div', { class: 'bar' }, [el('span', { style: 'width:100%' })]),
    ]);
  }
  private hudButton(icon: string, label: string, onClick: () => void): HTMLElement {
    // Icon and label are separate spans so a narrow/short screen can drop
    // the words and keep the glyph — with both in one span, hiding the
    // text hid the icon too and left a row of blank circles.
    const b = el('button', { class: 'hud-button' }, [
      el('span', { class: 'hud-button-icon' }, [], icon),
      el('span', { class: 'hud-button-text' }, [], label),
      el('span', { class: 'hud-alert' }, [], '!'),
    ]);
    b.addEventListener('click', onClick);
    return b;
  }

  // --------------- Panels ---------------
  private buildPanels(): void {
    this.panels.upgrades = this.makePanel('upgrades', 'Upgrades', 'Spend biomass to grow your cell.', false);
    this.panels.mutations = this.makePanel('mutations', 'Mutations', 'Spend DNA to evolve. Picks are permanent.', false);
    this.panels.codex = this.makePanel('codex', 'Codex', 'Everything you have encountered.', true);
    this.refreshUpgrades();
    this.refreshMutations();
    this.refreshCodex();
  }

  private makePanel(id: string, title: string, subtitle: string, right: boolean): HTMLElement {
    const panel = el('div', { class: `side-panel ${right ? 'right' : ''}`, id: `panel-${id}` });
    const close = el('button', { class: 'panel-close' }, [], '×');
    close.addEventListener('click', () => this.close(id));
    panel.appendChild(close);
    panel.appendChild(el('h2', {}, [], title));
    panel.appendChild(el('p', { style: 'opacity:0.65;font-size:12px;margin-top:2px;' }, [], subtitle));
    panel.appendChild(el('div', { class: 'panel-body' }));
    this.root.appendChild(panel);
    return panel;
  }

  toggle(id: string): void {
    const p = this.panels[id];
    if (!p) return;
    if (p.classList.contains('open')) { this.close(id); return; }
    Object.values(this.panels).forEach(pp => pp.classList.remove('open'));
    p.classList.add('open');
    if (id === 'upgrades') this.refreshUpgrades();
    if (id === 'mutations') this.refreshMutations();
    if (id === 'codex') { this.unseenCodex.clear(); this.refreshCodex(); this.refreshHudAlerts(); }
    this.setPaused(true);
  }
  close(id: string): void {
    this.panels[id]?.classList.remove('open');
    if (!this.anyPanelOpen() && !this.pauseOverlay.classList.contains('visible')) {
      this.setPaused(false);
    }
  }

  private anyPanelOpen(): boolean {
    return Object.values(this.panels).some(p => p.classList.contains('open'));
  }

  private setPaused(on: boolean): void {
    if (this.paused === on) return;
    this.paused = on;
    document.body.classList.toggle('show-cursor', on);
    bus.emit(on ? 'game:paused' : 'game:resumed', undefined);
  }

  private buildPauseOverlay(): void {
    this.pauseOverlay = el('div', { id: 'pause-overlay' }, [
      el('div', { class: 'po-card' }, [
        el('div', { class: 'po-sub' }, [], 'Paused'),
        el('h1', {}, [], 'Breath held'),
        el('p', {}, [], 'Press ESC to resume.'),
        (() => {
          const b = el('button', {}, [], 'Resume');
          b.addEventListener('click', () => this.hidePauseOverlay());
          return b;
        })(),
      ]),
    ]);
    this.root.appendChild(this.pauseOverlay);
  }

  private showPauseOverlay(): void {
    this.pauseOverlay.classList.add('visible');
    this.setPaused(true);
  }
  private hidePauseOverlay(): void {
    this.pauseOverlay.classList.remove('visible');
    if (!this.anyPanelOpen()) this.setPaused(false);
  }

  private wirePauseKeys(): void {
    window.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      // Priority: close any open panel, else close pause overlay, else open it.
      if (this.anyPanelOpen()) {
        Object.keys(this.panels).forEach(id => this.close(id));
        return;
      }
      if (this.pauseOverlay.classList.contains('visible')) {
        this.hidePauseOverlay();
      } else {
        this.showPauseOverlay();
      }
    });
  }

  refreshUpgrades(): void {
    const body = this.panels.upgrades.querySelector('.panel-body')!;
    body.innerHTML = '';
    for (const def of this.sys.upgrades.allDefs()) {
      const lvl = this.sys.upgrades.level(def.id);
      const cost = this.sys.upgrades.costOf(def.id);
      const can = this.sys.upgrades.canBuy(def.id);
      const card = el('div', { class: 'card' }, [
        el('div', { class: 'info' }, [
          el('div', { class: 'title' }, [], def.name),
          el('div', { class: 'desc' }, [], def.description),
          el('div', { class: 'cost' }, [], `Cost: ${cost.toLocaleString()} biomass`),
        ]),
        el('div', { style: 'display:flex;flex-direction:column;align-items:end;gap:6px;' }, [
          el('span', { class: 'lvl' }, [], `Lv ${lvl}`),
          (() => {
            const btn = el('button', can ? {} : { disabled: 'true' }, [], 'Buy');
            btn.addEventListener('click', () => {
              if (this.sys.upgrades.buy(def.id)) this.refreshUpgrades();
            });
            return btn;
          })(),
        ]),
      ]);
      body.appendChild(card);
    }
  }

  refreshMutations(): void {
    const body = this.panels.mutations.querySelector('.panel-body')!;
    body.innerHTML = '';

    const muts = this.sys.mutations;
    const slotsOwned = muts.slotsOwned();
    const slotsFilled = muts.slotsFilled();
    const ownedIds = muts.ownedIds();

    // slots row — render MAX_SLOTS cells; first slotsOwned are "open", the rest are locked
    const slotsRow = el('div', { class: 'slot-row' });
    for (let i = 0; i < MAX_SLOTS; i++) {
      const owned = i < slotsOwned;
      const filledId = owned ? ownedIds[i] : undefined;
      const def = filledId ? muts.getDef(filledId) : undefined;
      const cls = `slot ${def ? 'filled' : ''} ${owned ? '' : 'locked'}`;
      const label = !owned
        ? `🔒 Slot ${i + 1}`
        : (def ? `${def.name} · R${muts.rankOf(def.id)}` : `Slot ${i + 1}`);
      slotsRow.appendChild(el('div', { class: cls }, [], label));
    }
    body.appendChild(slotsRow);

    // Slot CTA — buy the next slot, if any.
    if (slotsOwned < MAX_SLOTS) {
      const cost = muts.slotCost();
      const can = player.dna >= cost;
      const btn = el('button', can ? { class: 'slot-cta' } : { class: 'slot-cta', disabled: 'true' }, [],
        `Unlock Slot ${slotsOwned + 1} — ${cost} DNA`);
      btn.addEventListener('click', () => {
        if (muts.buySlot()) { this.sys.upgrades.recompute(); this.refreshMutations(); }
      });
      body.appendChild(btn);
      body.appendChild(el('div', { class: 'slot-note' }, [],
        'A new slot lets you commit to another mutation. Picks and ranks are permanent for this run.'));
    } else if (slotsFilled >= slotsOwned) {
      body.appendChild(el('div', { class: 'slot-note' }, [],
        'All slots filled. Spend DNA to rank up your existing mutations.'));
    }

    // synergy status — fires on 2+ different mutations of one family owned
    const counts: Record<string, number> = {};
    for (const id of ownedIds) {
      const d = muts.getDef(id); if (d) counts[d.family] = (counts[d.family] ?? 0) + 1;
    }
    for (const fam of muts.allFamilies()) {
      const active = (counts[fam.id] ?? 0) >= 2;
      body.appendChild(el('div', { class: 'synergy-note' }, [],
        `${active ? '⬢' : '⬡'} ${fam.name} Synergy: ${fam.synergyDescription}${active ? ' — active' : ''}`));
    }

    // mutation cards, grouped by family
    for (const fam of muts.allFamilies()) {
      body.appendChild(el('h3', {}, [], fam.name));
      for (const m of muts.allDefs()) {
        if (m.family !== fam.id) continue;
        body.appendChild(this.mutationCard(m));
      }
    }
  }

  private mutationCard(m: MutationDef): HTMLElement {
    const muts = this.sys.mutations;
    const rank = muts.rankOf(m.id);
    const owned = rank > 0;
    const max = muts.maxRank(m.id);
    const atMax = rank >= max;

    const actions = el('div', { style: 'display:flex;flex-direction:column;gap:6px;align-items:end;' });
    if (!owned) {
      // First-pick — only allowed when there's an open slot.
      const cost = m.pickCost;
      const canPay = player.dna >= cost;
      const hasSlot = muts.hasOpenSlot();
      const can = canPay && hasSlot;
      const label = !hasSlot ? `Need slot · ${cost} DNA` : `Pick · ${cost} DNA`;
      const btn = el('button', can ? {} : { disabled: 'true' }, [], label);
      btn.addEventListener('click', () => {
        if (muts.buyRank(m.id)) { this.sys.upgrades.recompute(); this.refreshMutations(); }
      });
      actions.appendChild(btn);
    } else if (atMax) {
      actions.appendChild(el('span', { class: 'slot-locked-tag' }, [], `Rank ${max} · MAX`));
    } else {
      const cost = muts.rankCost(m.id);
      const can = player.dna >= cost;
      const btn = el('button', can ? {} : { disabled: 'true' }, [],
        `Rank ${rank} → ${rank + 1} · ${cost} DNA`);
      btn.addEventListener('click', () => {
        if (muts.buyRank(m.id)) { this.sys.upgrades.recompute(); this.refreshMutations(); }
      });
      actions.appendChild(btn);
    }

    return el('div', {
      class: `card mutation-card family-${m.family} ${owned ? 'equipped' : ''} ${owned ? '' : 'locked'}`,
    }, [
      el('div', { class: 'info' }, [
        el('div', { class: 'title' }, [], owned ? `${m.name} · R${rank}/${max}` : m.name),
        el('div', { class: 'desc' }, [], m.description),
        m.flavor ? el('div', { class: 'desc', style: 'font-style:italic;opacity:0.55;margin-top:4px;' }, [], m.flavor) : el('span'),
      ]),
      actions,
    ]);
  }

  /**
   * Build an <img> element for a codex entry's icon, or null if no icon
   * exists for that id. Uses a one-time canvas → PNG dataURL conversion
   * so the same icon can appear in both the codex menu and toasts.
   */
  private iconImg(entryId: string, sizePx: number): HTMLImageElement | null {
    const icons = this.sys.codexIcons;
    if (!icons) return null;
    const canvas = icons.get(entryId);
    if (!canvas) return null;
    let url = this.iconUrlCache.get(entryId);
    if (!url) {
      url = canvas.toDataURL('image/png');
      this.iconUrlCache.set(entryId, url);
    }
    const img = document.createElement('img');
    img.src = url;
    img.width = sizePx;
    img.height = sizePx;
    img.alt = '';
    img.className = 'codex-icon';
    return img;
  }

  refreshCodex(): void {
    const body = this.panels.codex.querySelector('.panel-body')!;
    body.innerHTML = '';
    const groups = this.sys.codex.entriesByCategory();
    const labels: Record<string, string> = {
      concept: 'Beginnings',
      food: 'Food & Nutrients',
      mutation: 'Mutations',
      predator: 'Predators',
      event: 'Environmental Events',
    };
    for (const cat of ['concept', 'food', 'mutation', 'predator', 'event']) {
      const list = groups[cat] ?? [];
      if (list.length === 0) continue;
      body.appendChild(el('h3', {}, [], labels[cat] ?? cat));
      for (const entry of list) {
        const unlocked = this.sys.codex.isUnlocked(entry.id);
        const headerRow = el('div', { class: 'codex-entry-head' }, []);
        // Icon — only shown when unlocked, since the entry is otherwise
        // hidden behind "??? Unknown" and revealing the silhouette would
        // give the species away.
        if (unlocked) {
          const icon = this.iconImg(entry.id, 48);
          if (icon) headerRow.appendChild(icon);
        }
        const titleCol = el('div', { class: 'codex-entry-title-col' }, [
          el('div', { class: 'title' }, [], unlocked ? entry.title : '??? Unknown'),
          el('div', { class: 'codex-entry-cat' }, [], entry.category),
        ]);
        headerRow.appendChild(titleCol);
        const block = el('div', { class: `codex-entry ${unlocked ? '' : 'locked'}` }, [headerRow]);
        if (unlocked) {
          block.appendChild(el('div', { class: 'flavor', style: 'margin-top:6px;font-size:13px;' }, [], entry.flavor));
          block.appendChild(el('div', { class: 'science' }, [], entry.science));
          if (entry.deepDive) {
            const dd = el('details', { class: 'deep-dive' }, [
              el('summary', { style: 'cursor:pointer;' }, [], 'Read deeper'),
              el('div', { style: 'margin-top:6px;' }, [], entry.deepDive),
            ]);
            block.appendChild(dd);
          }
        } else {
          block.appendChild(el('div', { class: 'flavor' }, [], 'Not yet encountered.'));
        }
        body.appendChild(block);
      }
    }
  }

  // --------------- Banner ---------------
  private buildBanner(): void {
    this.banner = el('div', { id: 'event-banner' }, [
      el('div', { class: 'eb-sub' }, [], 'Environmental Shift'),
      el('div', { class: 'eb-title' }),
      el('div', { class: 'eb-desc' }),
    ]);
    this.root.appendChild(this.banner);
  }
  private buildGoalBanner(): void {
    this.goalBanner = el('div', { id: 'goal-banner' }, [
      el('div', { class: 'gb-sub' }, [], 'Objective'),
      el('div', { class: 'gb-title' }),
      el('div', { class: 'gb-desc' }),
    ]);
    this.root.appendChild(this.goalBanner);
  }
  private showGoalBanner(title: string, subtitle: string, durationSec: number): void {
    (this.goalBanner.querySelector('.gb-title') as HTMLElement).textContent = title;
    (this.goalBanner.querySelector('.gb-desc') as HTMLElement).textContent = subtitle;
    this.goalBanner.classList.add('visible');
    this.goalBannerHide = durationSec;
  }
  private setBannerCountdown(def: EnvironmentalEventDef, secs: number): void {
    (this.banner.querySelector('.eb-sub') as HTMLElement).textContent = `Approaching — ${secs}s`;
    (this.banner.querySelector('.eb-title') as HTMLElement).textContent = def.name;
    (this.banner.querySelector('.eb-desc') as HTMLElement).textContent = def.description;
    this.banner.classList.add('visible');
    this.bannerHideTimer = 0;
  }
  private showBannerStarted(def: EnvironmentalEventDef): void {
    (this.banner.querySelector('.eb-sub') as HTMLElement).textContent = 'Event Active';
    (this.banner.querySelector('.eb-title') as HTMLElement).textContent = def.name;
    (this.banner.querySelector('.eb-desc') as HTMLElement).textContent = def.description;
    this.banner.classList.add('visible');
    this.bannerHideTimer = 2.5;
  }

  // --------------- Toasts ---------------
  private buildToasts(): void {
    this.toasts = el('div', { id: 'codex-toasts' });
    this.root.appendChild(this.toasts);
  }
  private showCodexToast(entry: CodexEntry): void {
    const t = el('div', { class: 'codex-toast' }, []);
    const icon = this.iconImg(entry.id, 36);
    if (icon) {
      t.appendChild(icon);
    } else {
      t.appendChild(el('span', { class: 'book' }, [], '📖'));
    }
    t.appendChild(el('div', { class: 'codex-toast-text' }, [
      el('div', { class: 'codex-toast-label' }, [], 'New Codex'),
      el('div', { class: 'codex-toast-title' }, [], entry.title),
    ]));
    this.toasts.appendChild(t);
    setTimeout(() => t.classList.add('fading'), 3400);
    setTimeout(() => t.remove(), 3900);
  }

  // --------------- Stage complete ---------------
  private buildStageComplete(): void {
    this.stageCompleteEl = el('div', { id: 'stage-complete' }, [
      el('div', { class: 'sc-card' }, [
        el('div', { class: 'sc-sub' }, [], 'Stage 1 — Unicell'),
        el('h1', {}, [], 'You are no longer alone.'),
        el('p', { style: 'opacity:0.78;margin-bottom:20px;max-width:460px;margin-left:auto;margin-right:auto;' }, [],
          'You have grown, absorbed, survived. Multicellularity awaits — your DNA carries forward, your codex is preserved.'),
        el('div', { class: 'sc-stats' }),
        el('div', { class: 'sc-score', style: 'margin:4px 0 16px;font-size:14px;font-weight:600;color:#fadf7c;' }),
        el('button', {}, [], 'Evolve into Phase 2'),
      ]),
    ]);
    this.root.appendChild(this.stageCompleteEl);
    const btn = this.stageCompleteEl.querySelector('button')!;
    btn.addEventListener('click', () => {
      // Guard: a focused button still fires click on Enter/Space even when
      // the overlay is hidden (pointer-events:none blocks mouse, not keys).
      // Ignore phantom activations so transition can't be triggered mid-run.
      if (!this.stageCompleteEl.classList.contains('visible')) return;
      this.stageCompleteEl.classList.remove('visible');
      document.body.classList.remove('show-cursor');
      btn.blur();
      // Phase 2: this is now an inter-stage hop, not an in-stage restart.
      // Game.ts listens for `stage:advance` and asks StageManager to
      // resetRunProgress(nextId) and start the successor.
      bus.emit('stage:advance', undefined);
    });
  }
  private showStageComplete(summary: Record<string, unknown>): void {
    const grid = this.stageCompleteEl.querySelector('.sc-stats') as HTMLElement;
    grid.innerHTML = '';
    const time = Number(summary.time ?? 0);
    const speedTier = String(summary.speedTier ?? '—');
    const speedMult = Number(summary.speedMult ?? 1);
    const baseDna = Number(summary.dnaEarned ?? 0);
    const speedBonus = Number(summary.speedBonusDna ?? 0);
    const codexBonus = Number(summary.codexBonusDna ?? 0);
    const totalDnaCarry = Number(summary.totalDnaCarry ?? baseDna);
    const headStart = Number(summary.biomassHeadStart ?? 0);

    const runStats: [string, string][] = [
      ['Biomass earned', Math.floor(Number(summary.biomass ?? 0)).toLocaleString()],
      ['Time', `${Math.floor(time / 60)}:${String(Math.floor(time) % 60).padStart(2, '0')}`],
      ['Codex unlocked', String(summary.codexUnlocked ?? 0)],
      ['Events survived', String(summary.eventsSurvived ?? 0)],
    ];
    const carryStats: [string, string][] = [
      ['Speed tier', `${speedTier} (×${speedMult.toFixed(1)})`],
      ['Base DNA', String(baseDna)],
      ['+ Speed bonus DNA', `+${speedBonus}`],
      ['+ Codex bonus DNA', `+${codexBonus}`],
      ['DNA carried to Phase 2', String(totalDnaCarry)],
      ['Biomass head-start', headStart.toLocaleString()],
    ];

    grid.appendChild(el('div', { class: 'sc-section-title' }, [], 'This run'));
    for (const [k, v] of runStats) {
      grid.appendChild(el('div', {}, [
        el('div', { class: 'k' }, [], k),
        el('div', { class: 'v' }, [], v),
      ]));
    }
    grid.appendChild(el('div', { class: 'sc-section-title' }, [], 'Carries to Phase 2'));
    for (const [k, v] of carryStats) {
      grid.appendChild(el('div', {}, [
        el('div', { class: 'k' }, [], k),
        el('div', { class: 'v' }, [], v),
      ]));
    }
    (this.stageCompleteEl.querySelector('.sc-score') as HTMLElement).textContent = 'Submitting score…';
    this.stageCompleteEl.classList.add('visible');
    document.body.classList.add('show-cursor');
  }

  private buildCursor(): void {
    this.deathOverlay = el('div', { class: 'death-overlay' });
    this.root.appendChild(this.deathOverlay);
    this.deathBanner = el('div', { class: 'death-banner' }, [
      el('div', { class: 'db-title' }, [], 'Cell Absorbed'),
      el('div', { class: 'db-sub' }, [], 'Respawning…'),
    ]);
    this.root.appendChild(this.deathBanner);
  }

  /** Touch-only on-screen ability buttons for Pulse and Dash. */
  private buildAbilityPad(): void {
    if (!IS_TOUCH) return;
    const pulseBtn = el('button', { class: 'ability-btn pulse', 'aria-label': 'Pulse' }, [
      el('span', { class: 'ability-icon' }, [], '◉'),
      el('span', { class: 'ability-label' }, [], 'PULSE'),
    ]);
    const dashBtn = el('button', { class: 'ability-btn dash', 'aria-label': 'Dash' }, [
      el('span', { class: 'ability-icon' }, [], '➤'),
      el('span', { class: 'ability-label' }, [], 'DASH'),
    ]);
    // Use pointerdown for snappy response (vs click's synthesized 300ms delay).
    const wire = (btn: HTMLElement, fn?: () => void) => {
      btn.addEventListener('pointerdown', (e) => { e.preventDefault(); e.stopPropagation(); fn?.(); });
      btn.addEventListener('click', (e) => e.stopPropagation());
    };
    wire(pulseBtn, this.sys.onPulse);
    wire(dashBtn, this.sys.onDash);
    const pad = el('div', { id: 'ability-pad' }, [pulseBtn, dashBtn]);
    this.root.appendChild(pad);
  }

  private wireEvents(): void {
    // Paint initial HUD values from the live player state. hydrateFromSave()
    // runs before UI is built, so we'd otherwise display 0 for biomass/DNA/HP
    // until the first mutation of each — DNA in particular appears to "jump"
    // from 0 to the saved value on the first pickup.
    this.biomassVal.textContent = formatBig(player.biomass);
    this.dnaVal.textContent = player.dna.toString();
    this.hpVal.textContent = `${Math.round(player.hp)}/${Math.round(player.stats.hpMax)}`;
    this.hpBar.style.width = `${(player.hp / player.stats.hpMax) * 100}%`;
    this.atpVal.textContent = `${Math.round(player.atp)}/${Math.round(player.stats.atpMax)}`;
    this.atpBar.style.width = `${(player.atp / player.stats.atpMax) * 100}%`;

    this.removers.push(bus.on('biomass:changed', ({ value }) => {
      this.biomassVal.textContent = formatBig(value);
      this.updateGoalBar(value);
    }));
    // Cheat: drain biomass into whatever upgrades the system can buy
    // right now, looping until no upgrade is affordable. UpgradeSystem.buy
    // already spends biomass and emits 'upgrade:bought' per purchase.
    this.removers.push(bus.on('cheat:autoBuyUpgrades', () => {
      const defs = this.sys.upgrades.allDefs();
      let bought = 0;
      while (true) {
        let anyBought = false;
        for (const def of defs) {
          if (this.sys.upgrades.canBuy(def.id) && this.sys.upgrades.buy(def.id)) {
            anyBought = true;
            bought++;
          }
        }
        if (!anyBought) break;
      }
      if (bought > 0) this.refreshUpgrades();
    }));
    this.removers.push(bus.on('dna:changed', ({ value }) => {
      this.dnaVal.textContent = value.toString();
    }));
    this.removers.push(bus.on('hp:changed', ({ value, max }) => {
      this.hpVal.textContent = `${Math.round(value)}/${Math.round(max)}`;
      this.hpBar.style.width = `${(value / max) * 100}%`;
      this.deathOverlay.classList.toggle('active', value < max * 0.25);
    }));
    this.removers.push(bus.on('atp:changed', ({ value, max }) => {
      this.atpVal.textContent = `${Math.round(value)}/${Math.round(max)}`;
      this.atpBar.style.width = `${(value / max) * 100}%`;
    }));
    const comboMain = this.comboEl.querySelector('.combo-main') as HTMLElement;
    const comboStreak = this.comboEl.querySelector('.combo-streak') as HTMLElement;
    const refreshComboVisible = (): void => {
      const any = comboMain.childNodes.length > 0 || comboStreak.childNodes.length > 0;
      this.comboEl.classList.toggle('visible', any);
    };
    this.removers.push(bus.on('combo:changed', ({ combo, multiplier }) => {
      if (combo <= 1) {
        comboMain.replaceChildren();
      } else {
        comboMain.innerHTML = `Combo <strong>×${combo}</strong> · ${multiplier.toFixed(2)}x`;
      }
      refreshComboVisible();
    }));
    // Same-type food streak indicator. Phase 1 gates this on halo-off
    // (streak only ticks when the player commits to manual contact).
    // Streak silently builds for the first STREAK_DISPLAY_MIN-1 absorbs
    // — only past that does the UI reveal it, because that's when the
    // multiplier kicks in.
    const STREAK_DISPLAY_MIN = 5;
    let streakHideTimer: number | undefined;
    const clearStreak = (): void => {
      if (streakHideTimer) { clearTimeout(streakHideTimer); streakHideTimer = undefined; }
      comboStreak.replaceChildren();
      this.comboEl.classList.remove('streak');
      refreshComboVisible();
    };
    this.removers.push(bus.on('combo:streak', ({ foodId, count, multiplier }) => {
      if (streakHideTimer) { clearTimeout(streakHideTimer); streakHideTimer = undefined; }
      if (!foodId || count < STREAK_DISPLAY_MIN) {
        clearStreak();
        return;
      }
      this.comboEl.classList.add('streak');
      comboStreak.replaceChildren();
      // Food icon — same canvas-rendered silhouette as the codex panel.
      const icon = this.iconImg(`food.${foodId}`, 22);
      if (icon) comboStreak.appendChild(icon);
      const label = foodId.charAt(0).toUpperCase() + foodId.slice(1);
      const text = document.createElement('span');
      text.className = 'streak-text';
      text.innerHTML = `${label} streak <strong>×${count}</strong> · ${multiplier.toFixed(2)}x`;
      comboStreak.appendChild(text);
      refreshComboVisible();
      // Auto-hide if no further streak event arrives in 5s. Hiding doesn't
      // reset the underlying counter — the next absorb refreshes the UI.
      streakHideTimer = window.setTimeout(() => clearStreak(), 5000);
    }));
    this.removers.push(bus.on('event:warning', ({ eventId }) => {
      const def = this.sys.events[eventId];
      if (def) this.setBannerCountdown(def, 20);
    }));
    this.removers.push(bus.on('event:started', ({ eventId }) => {
      const def = this.sys.events[eventId];
      if (def) this.showBannerStarted(def);
    }));
    this.removers.push(bus.on('event:ended', () => {
      this.banner.classList.remove('visible');
    }));
    this.removers.push(bus.on('stage:complete', ({ summary }) => this.showStageComplete(summary)));
    this.removers.push(bus.on('score:submitted', (p) => {
      if (p.stageId !== 'cell') return;
      (this.stageCompleteEl.querySelector('.sc-score') as HTMLElement).textContent = describeScoreResult(p);
    }));
    this.removers.push(bus.on('stage:goalBanner', ({ title, subtitle, durationSec }) => {
      this.showGoalBanner(title, subtitle, durationSec);
    }));
    this.removers.push(bus.on('player:died', () => this.deathBanner.classList.add('visible')));
    this.removers.push(bus.on('save:loaded', () => this.deathBanner.classList.remove('visible')));
    this.sys.codex.setNotifier((entry) => {
      this.unseenCodex.add(entry.id);
      this.showCodexToast(entry);
      this.refreshHudAlerts();
    });
  }

  /** Tear down before a stage transition so handlers don't pile up. */
  destroy(): void {
    for (const off of this.removers) off();
    this.removers = [];
    this.sys.codex.setNotifier(() => {});
    if (this.root.parentElement) this.root.parentElement.removeChild(this.root);
  }
}

function formatBig(n: number): string {
  // Always show the full integer with thousands separators — the
  // exact biomass count matters for goal comparisons (e.g. 249,999
  // vs 250,000), and abbreviating to "250.0K" hid that delta.
  return Math.floor(n).toLocaleString();
}

function el(tag: string, attrs: Record<string, string> = {}, children: Node[] = [], text?: string): HTMLElement {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined) continue;
    if (k === 'class') e.className = v;
    else e.setAttribute(k, v);
  }
  if (text) e.textContent = text;
  for (const c of children) if (c) e.appendChild(c);
  return e;
}
